import { useState, useCallback } from 'react';
import { useDraggable } from '@dnd-kit/core';
import { getCardImageUri } from '@mtg-deck-tool/shared';
import type { CardInstance } from '@mtg-deck-tool/shared';
import { usePlaytesterStore } from '../../stores/playtester-store.js';

interface LibraryCardRowProps {
  instance: CardInstance;
  position: number;
  onHover: (card: CardInstance | null) => void;
}

function LibraryCardRow({ instance, position, onHover }: LibraryCardRowProps) {
  const artUri = getCardImageUri(instance.card, 'artCrop');
  const { attributes, listeners, setNodeRef, isDragging } = useDraggable({
    id: `library-${instance.instanceId}`,
    data: { instanceId: instance.instanceId, zone: 'library' },
  });

  return (
    <div
      ref={setNodeRef}
      {...listeners}
      {...attributes}
      onMouseEnter={() => onHover(instance)}
      onMouseLeave={() => onHover(null)}
      className={`flex items-center gap-2 px-2 py-1.5 rounded hover:bg-gray-700/60 cursor-grab active:cursor-grabbing transition ${
        isDragging ? 'opacity-40' : ''
      }`}
    >
      <span className="text-[10px] text-gray-600 w-5 text-right shrink-0">{position}</span>
      {artUri ? (
        <img src={artUri} alt={instance.card.name} className="w-8 h-8 object-cover rounded shrink-0 border border-gray-700" />
      ) : (
        <div className="w-8 h-8 bg-gray-800 rounded shrink-0 border border-gray-700" />
      )}
      <div className="flex-1 min-w-0">
        <div className="text-sm text-gray-200 truncate">{instance.card.name}</div>
        <div className="text-[10px] text-gray-500 truncate">{instance.card.typeLine}</div>
      </div>
    </div>
  );
}

export default function LibraryPanel() {
  const library = usePlaytesterStore((s) => s.library);
  const millInput = usePlaytesterStore((s) => s.millInput);
  const setMillInput = usePlaytesterStore((s) => s.setMillInput);
  const millTopX = usePlaytesterStore((s) => s.millTopX);
  const openScry = usePlaytesterStore((s) => s.openScry);
  const setOpenPanel = usePlaytesterStore((s) => s.setOpenPanel);
  const setHoveredCard = usePlaytesterStore((s) => s.setHoveredCard);

  const [filter, setFilter] = useState('');
  const [scryCount, setScryCount] = useState(3);
  // Panel rows update the global hoveredCard — the global CardHoverPreview
  // handles display (no inline preview here).
  const handleHover = useCallback(
    (instance: CardInstance | null) => {
      setHoveredCard(instance?.card ?? null);
    },
    [setHoveredCard],
  );

  const filtered = filter
    ? library.map((inst, idx) => ({ inst, idx })).filter(({ inst }) =>
        inst.card.name.toLowerCase().includes(filter.toLowerCase())
      )
    : library.map((inst, idx) => ({ inst, idx }));

  function handleScry() {
    const n = Math.min(scryCount, library.length);
    if (n > 0) {
      openScry(n);
      // ScryPanel will open automatically since openScry sets openPanel: 'scry'
    }
  }

  return (
    <div className="fixed inset-0 z-[100] flex items-center justify-center bg-black/60" onClick={() => setOpenPanel(null)}>
      <div
        className="relative bg-gray-900 border border-gray-700 rounded-xl shadow-2xl w-[520px] max-h-[80vh] flex flex-col"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className="flex items-center justify-between px-4 py-3 border-b border-gray-800 shrink-0">
          <h2 className="text-sm font-semibold text-white">
            Library <span className="text-gray-500 font-normal">({library.length} cards)</span>
          </h2>
          <button
            onClick={() => setOpenPanel(null)}
            className="text-gray-500 hover:text-white transition text-lg leading-none"
          >
            ✕
          </button>
        </div>

        {/* Controls */}
        <div className="flex items-center gap-3 px-3 py-2 border-b border-gray-800 shrink-0 flex-wrap">
          {/* Search */}
          <input
            type="text"
            placeholder="Search..."
            value={filter}
            onChange={(e) => setFilter(e.target.value)}
            className="flex-1 min-w-[100px] bg-gray-800 border border-gray-700 rounded px-2 py-1 text-sm text-white outline-none focus:border-orange-500 transition"
            autoFocus
          />
          {/* Scry control */}
          <div className="flex items-center gap-1">
            <span className="text-xs text-gray-500">Scry</span>
            <input
              type="number"
              min={1}
              max={library.length || 1}
              value={scryCount}
              onChange={(e) => setScryCount(Math.max(1, parseInt(e.target.value, 10) || 1))}
              className="w-10 text-center text-xs bg-gray-800 border border-gray-700 rounded text-white outline-none"
            />
            <button
              onClick={handleScry}
              disabled={library.length === 0}
              className="text-xs px-2 py-1 bg-blue-700 hover:bg-blue-600 disabled:bg-gray-800 disabled:text-gray-600 text-white rounded transition"
            >
              Peek
            </button>
          </div>
          {/* Mill control */}
          <div className="flex items-center gap-1">
            <span className="text-xs text-gray-500">Mill</span>
            <input
              type="number"
              min={1}
              max={library.length || 1}
              value={millInput}
              onChange={(e) => setMillInput(Math.max(1, parseInt(e.target.value, 10) || 1))}
              className="w-10 text-center text-xs bg-gray-800 border border-gray-700 rounded text-white outline-none"
            />
            <button
              onClick={() => { millTopX(millInput); setOpenPanel(null); }}
              disabled={library.length === 0}
              className="text-xs px-2 py-1 bg-red-800 hover:bg-red-700 disabled:bg-gray-800 disabled:text-gray-600 text-white rounded transition"
            >
              Mill
            </button>
          </div>
        </div>

        {/* Card list — hover feeds global CardHoverPreview (no inline preview) */}
        <div className="flex-1 overflow-y-auto py-1">
          {filtered.length === 0 ? (
            <div className="text-center text-gray-600 text-sm py-8">
              {filter ? 'No matches' : 'Empty library'}
            </div>
          ) : (
            filtered.map(({ inst, idx }) => (
              <LibraryCardRow
                key={inst.instanceId}
                instance={inst}
                position={idx + 1}
                onHover={handleHover}
              />
            ))
          )}
        </div>
      </div>
    </div>
  );
}
